import * as THREE from 'three/webgpu';
import { PickupType } from '../../shared/types.js';
import { getAlgorithmCardVisual } from './algorithmCardAssets';
import { getPickupSpriteVisual, type PickupSpriteVisual } from './pickupSpriteAssets';
import { PickupGlow } from './PickupGlow';

interface Vec3 {
    x: number;
    y: number;
    z: number;
}

interface PickupData {
    id: string;
    pickupType: number;
    subType: number;
    tier: number;
    position: Vec3;
    collected: boolean;
}

const TIER_COLORS = [0xffffff, 0x00c8ff, 0xffaa00];
const TIER_EMISSIVE = [0.3, 0.5, 1.0];
const _whiteColor = new THREE.Color(0xffffff);

/** Hash a pickup id to a stable phase seed so two pickups spawned at the same
 *  moment don't pulse in lockstep. */
function hashId(id: string): number {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) {
        h ^= id.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) % 997;
}

interface PickupSpriteEntry {
    pickupType: number;
    subType: number;
    baseSprite: THREE.Sprite;
    baseMaterial: THREE.SpriteNodeMaterial;
    glowSprite: THREE.Sprite;
    glowMaterial: THREE.SpriteNodeMaterial;
    iconSprite?: THREE.Sprite;
    iconMaterial?: THREE.SpriteNodeMaterial;
    /** Pulsing rim-light halo so pickups read from a distance. */
    pulseGlow?: PickupGlow;
}

interface FadeBaseOpacity {
    base: number;
    glow: number;
    icon?: number;
    pulse?: number;
}

/* ── Renderer ────────────────────────────────────────────────── */

const COLLECT_FADE_MS = 150;

export class PickupRenderer {
    private pickupGroup: THREE.Group;
    private meshes: Map<string, THREE.Group> = new Map();
    private textureLoader = new THREE.TextureLoader();
    private textures = new Map<string, THREE.Texture>();
    private time = 0;
    /** Most recent deltaMs from update() — forwarded into PickupGlow.update(). */
    private lastDeltaMs = 16;
    /** Per-id elapsed-ms for the 150ms collect shrink/fade. Bridges the gap
     *  between contact and the EffectsManager.showPickupSparkle burst so the
     *  pickup doesn't pop out in a single frame. */
    private collectingSinceMs: Map<string, number> = new Map();
    /** Opacity at the moment of collect; the fade lerps from this base toward
     *  0 (multiplying a decreasing value by k every frame would compound to
     *  exponential decay, not a linear 150ms ramp). */
    private fadeBaseOpacity: Map<string, FadeBaseOpacity> = new Map();

    constructor(scene: THREE.Scene) {
        this.pickupGroup = new THREE.Group();
        scene.add(this.pickupGroup);
    }

    // Sprites billboard automatically; retained for renderer API parity.
    setCameraPosition(_pos: THREE.Vector3): void {}

    update(pickups: PickupData[], deltaMs: number = 16): void {
        this.time += deltaMs / 1000;
        this.lastDeltaMs = deltaMs;
        const seenIds = new Set<string>();

        for (const pickup of pickups) {
            seenIds.add(pickup.id);
            if (pickup.collected) {
                if (this.meshes.has(pickup.id) && !this.collectingSinceMs.has(pickup.id)) {
                    this.collectingSinceMs.set(pickup.id, 0);
                }
                continue;
            }

            let mesh = this.meshes.get(pickup.id);
            const entry = mesh?.userData.pickupSpriteEntry as PickupSpriteEntry | undefined;
            if (
                mesh &&
                entry &&
                (entry.pickupType !== pickup.pickupType || entry.subType !== pickup.subType)
            ) {
                this.disposeMesh(mesh);
                this.pickupGroup.remove(mesh);
                this.meshes.delete(pickup.id);
                mesh = undefined;
            }

            if (!mesh) {
                mesh = this.createPickupMesh(pickup);
                this.meshes.set(pickup.id, mesh);
                this.pickupGroup.add(mesh);
            }

            mesh.position.set(
                pickup.position.x,
                pickup.position.y + Math.sin(this.time * 2 + pickup.position.x) * 0.5,
                pickup.position.z
            );
            this.updateSpriteMesh(mesh, pickup);
        }

        this.collectingSinceMs.forEach((elapsed, id) => {
            const mesh = this.meshes.get(id);
            if (!mesh) {
                this.collectingSinceMs.delete(id);
                this.fadeBaseOpacity.delete(id);
                return;
            }
            const next = elapsed + deltaMs;
            if (next >= COLLECT_FADE_MS) {
                this.disposeMesh(mesh);
                this.pickupGroup.remove(mesh);
                this.meshes.delete(id);
                this.collectingSinceMs.delete(id);
                this.fadeBaseOpacity.delete(id);
                return;
            }
            if (!this.fadeBaseOpacity.has(id)) this.captureFadeBaseOpacity(id, mesh);
            this.collectingSinceMs.set(id, next);
            const k = 1 - next / COLLECT_FADE_MS;
            mesh.scale.setScalar(k);
            this.applyCollectFadeOpacity(id, mesh, k);
        });

        this.meshes.forEach((mesh, id) => {
            if (!seenIds.has(id) && !this.collectingSinceMs.has(id)) {
                this.disposeMesh(mesh);
                this.pickupGroup.remove(mesh);
                this.meshes.delete(id);
            }
        });
    }

    private captureFadeBaseOpacity(id: string, group: THREE.Group): void {
        const entry = group.userData.pickupSpriteEntry as PickupSpriteEntry | undefined;
        if (!entry) return;
        this.fadeBaseOpacity.set(id, {
            base: entry.baseMaterial.opacity,
            glow: entry.glowMaterial.opacity,
            icon: entry.iconMaterial?.opacity,
            pulse: entry.pulseGlow?.material.opacity,
        });
    }

    private applyCollectFadeOpacity(id: string, group: THREE.Group, k: number): void {
        const entry = group.userData.pickupSpriteEntry as PickupSpriteEntry | undefined;
        const base = this.fadeBaseOpacity.get(id);
        if (!entry || !base) return;
        entry.baseMaterial.opacity = base.base * k;
        entry.glowMaterial.opacity = base.glow * k;
        if (entry.iconMaterial && base.icon !== undefined) {
            entry.iconMaterial.opacity = base.icon * k;
        }
        if (entry.pulseGlow && base.pulse !== undefined) {
            entry.pulseGlow.material.opacity = base.pulse * k;
        }
    }

    private createPickupMesh(pickup: PickupData): THREE.Group {
        const group = new THREE.Group();
        group.userData.pickupId = pickup.id;
        const visual = getPickupSpriteVisual(pickup.pickupType);
        const baseTexture = this.getTexture(visual.url);
        const baseMaterial = this.createSpriteMaterial(baseTexture);
        const glowMaterial = this.createSpriteMaterial(baseTexture, true);

        const glowSprite = new THREE.Sprite(glowMaterial as unknown as THREE.SpriteMaterial);
        glowSprite.raycast = () => {};
        glowSprite.frustumCulled = false;
        glowSprite.renderOrder = 4;
        group.add(glowSprite);

        const baseSprite = new THREE.Sprite(baseMaterial as unknown as THREE.SpriteMaterial);
        baseSprite.frustumCulled = false;
        baseSprite.renderOrder = 5;
        baseSprite.userData.pickupId = pickup.id;
        group.add(baseSprite);

        // Halo reuses baseTexture (no extra GPU upload) and parents to the
        // pickup group so it bobs/pulses with the rest of the pickup.
        const tierColor = new THREE.Color(TIER_COLORS[pickup.tier] ?? 0xffffff);
        const pulseGlow = new PickupGlow(group, baseTexture, tierColor, hashId(pickup.id));

        const entry: PickupSpriteEntry = {
            pickupType: pickup.pickupType,
            subType: pickup.subType,
            baseSprite,
            baseMaterial,
            glowSprite,
            glowMaterial,
            pulseGlow,
        };

        if (pickup.pickupType === PickupType.Algorithm) {
            const cardTexture = this.getTexture(getAlgorithmCardVisual(pickup.subType).url);
            const iconMaterial = this.createSpriteMaterial(cardTexture);
            iconMaterial.opacity = 0.95;
            const iconSprite = new THREE.Sprite(iconMaterial as unknown as THREE.SpriteMaterial);
            iconSprite.frustumCulled = false;
            iconSprite.raycast = () => {};
            iconSprite.renderOrder = 6;
            group.add(iconSprite);
            entry.iconSprite = iconSprite;
            entry.iconMaterial = iconMaterial;
        }

        group.userData.pickupSpriteEntry = entry;
        this.updateSpriteMesh(group, pickup);

        return group;
    }

    private updateSpriteMesh(group: THREE.Group, pickup: PickupData): void {
        const entry = group.userData.pickupSpriteEntry as PickupSpriteEntry | undefined;
        if (!entry) return;

        const visual: PickupSpriteVisual = getPickupSpriteVisual(pickup.pickupType);
        const tierColor = new THREE.Color(TIER_COLORS[pickup.tier] ?? 0xffffff);
        const emissive = TIER_EMISSIVE[pickup.tier] ?? 0.3;
        const pulse = 1 + Math.sin(this.time * 2.5 + pickup.position.x) * 0.06;
        const width = visual.width * pulse;
        const height = visual.height * pulse;
        const yOffset = visual.yOffset ?? 0;
        const rotation = this.time * 1.2 + pickup.position.x * 0.03;

        entry.baseSprite.position.set(0, yOffset, 0);
        entry.baseSprite.scale.set(width, height, 1);
        entry.baseMaterial.color.copy(_whiteColor).lerp(tierColor, visual.tintStrength ?? 0.16);
        entry.baseMaterial.opacity = 0.94;
        entry.baseMaterial.rotation = rotation;

        const glowScale = (visual.glowScale ?? 1.16) + emissive * 0.1;
        entry.glowSprite.position.copy(entry.baseSprite.position);
        entry.glowSprite.scale.set(width * glowScale, height * glowScale, 1);
        entry.glowMaterial.color.copy(tierColor);
        entry.glowMaterial.opacity = 0.18 + emissive * 0.24;
        entry.glowMaterial.rotation = rotation;

        if (entry.iconSprite && entry.iconMaterial) {
            entry.iconSprite.position.set(0, yOffset + 0.02, 0.01);
            entry.iconSprite.scale.set(width * 0.42, height * 0.42, 1);
            entry.iconMaterial.rotation = 0;
            entry.iconMaterial.opacity = 0.98;
        }

        if (entry.pulseGlow) {
            entry.pulseGlow.update(
                this.lastDeltaMs,
                { baseWidth: width, baseHeight: height, yOffset },
                tierColor,
            );
        }
    }

    private createSpriteMaterial(texture: THREE.Texture, glow = false): THREE.SpriteNodeMaterial {
        return new THREE.SpriteNodeMaterial({
            map: texture,
            color: 0xffffff,
            transparent: true,
            opacity: glow ? 0.35 : 1,
            alphaTest: 0.02,
            depthWrite: false,
            depthTest: true,
            sizeAttenuation: true,
            toneMapped: false,
            blending: glow ? THREE.AdditiveBlending : THREE.NormalBlending,
        });
    }

    private getTexture(url: string): THREE.Texture {
        let texture = this.textures.get(url);
        if (!texture) {
            texture = this.textureLoader.load(url);
            texture.colorSpace = THREE.SRGBColorSpace;
            texture.minFilter = THREE.LinearMipmapLinearFilter;
            texture.magFilter = THREE.LinearFilter;
            texture.generateMipmaps = true;
            this.textures.set(url, texture);
        }
        return texture;
    }

    private disposeMesh(group: THREE.Group): void {
        // Halo dispose() detaches its sprite first; otherwise the traverse
        // below would double-dispose the shared SpriteNodeMaterial.
        const entry = group.userData.pickupSpriteEntry as PickupSpriteEntry | undefined;
        if (entry?.pulseGlow) {
            entry.pulseGlow.dispose();
            entry.pulseGlow = undefined;
        }
        group.traverse((child) => {
            if (child instanceof THREE.Sprite) {
                if (Array.isArray(child.material)) {
                    child.material.forEach((m) => m.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    }

    dispose(): void {
        this.meshes.forEach((mesh) => {
            this.disposeMesh(mesh);
            this.pickupGroup.remove(mesh);
        });
        this.meshes.clear();
        this.collectingSinceMs.clear();
        this.fadeBaseOpacity.clear();
        this.textures.forEach((texture) => texture.dispose());
        this.textures.clear();
    }
}
